import React, { useState } from "react";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, BookOpen, Clapperboard, Film, Scissors } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function ScriptAwarenessGuide() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="ml-2 gap-2 text-primary hover:text-primary/80"
          onClick={() => setOpen(true)}
        >
          <BookOpen className="w-4 h-4" />
          {t("scriptGuide.btn", "剧本指南")}
        </Button>
      </DialogTrigger>
      
      <DialogContent
        className="max-w-2xl w-[95vw] sm:w-full max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden rounded-2xl bg-slate-50 dark:bg-slate-950"
      >
        <DialogHeader className="px-6 py-4 border-b bg-white dark:bg-slate-900 shrink-0 z-10">
          <DialogTitle className="text-xl font-bold text-center flex items-center justify-center gap-2">
            <Clapperboard className="w-5 h-5 text-amber-500" />
            {t("scriptGuide.title", "你是自己人生的导演")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50 dark:bg-slate-950">
          <Tabs defaultValue="philosophy" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-200 dark:bg-slate-800">
              <TabsTrigger value="philosophy">{t("scriptGuide.tab1", "核心理念")}</TabsTrigger>
              <TabsTrigger value="archetypes">{t("scriptGuide.tab2", "角色图鉴")}</TabsTrigger>
            </TabsList>

            {/* --- Tab 1: 核心理念 (Updated Version) --- */}
            <TabsContent value="philosophy" className="animate-in fade-in-50 slide-in-from-bottom-2 focus-visible:outline-none">
              <div className="space-y-8 max-w-prose mx-auto pb-8">
                
                {/* Header Section */}
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-black text-slate-800 dark:text-slate-100 tracking-tight">
                    {t("scriptGuide.philosophy.mainTitle", "关闭人生的“自动驾驶”模式")}
                  </h2>
                  <p className="text-muted-foreground font-medium">
                    {t("scriptGuide.philosophy.subTitle", "哪怕只是一只脏碗，都有三种命运")}
                  </p>
                </div>

                <div className="w-full h-px bg-gradient-to-r from-transparent via-slate-300 dark:via-slate-700 to-transparent" />

                {/* Section 1: 生活 vs 被生活 */}
                <section>
                  <h3 className="font-bold text-lg text-primary flex items-center gap-2 mb-3">
                    <Film className="w-4 h-4" />
                    {t("scriptGuide.philosophy.sec1.title", "你是在“生活”，还是在“被生活”？")}
                  </h3>
                  <div className="text-muted-foreground leading-relaxed space-y-4">
                    <p>
                      {t("scriptGuide.philosophy.sec1.p1", "绝大多数时候，我们以为自己是清醒的，但其实我们只是在梦游。早晨被闹钟叫醒，像机器一样刷牙洗脸；面对老板的质问，下意识地开始推卸责任或瑟瑟发抖；看到伴侣没洗的碗，还没经过大脑，愤怒的指责就已经脱口而出。")}
                    </p>
                    <p>
                      {t("scriptGuide.philosophy.sec1.p2", "我们活得像个 NPC（非玩家角色），按照设定好的程序，对外界的刺激做出千篇一律的反应。但 Inner Gem 想告诉你一个被你遗忘的真相：你不是剧本里的演员，你是手握剪刀的导演。")}
                    </p>
                  </div>
                </section>

                {/* Section 2: 剧本示例 */}
                <section className="bg-white dark:bg-slate-900 rounded-xl p-5 border shadow-sm">
                  <h3 className="font-bold text-lg text-primary mb-4 flex items-center gap-2">
                    <Scissors className="w-5 h-5 shrink-0" />
                    {t("scriptGuide.philosophy.sec2.title", "哪怕是一件小事，剧本也从未写死")}
                  </h3>
                  <p className="text-muted-foreground mb-4 text-sm">
                    {t("scriptGuide.philosophy.sec2.intro", "以“伴侣没洗碗”这件小事为例，在哪怕一秒钟的缝隙里，你其实拥有无数个平行的剧本：")}
                  </p>
                  <ul className="space-y-3 text-sm">
                    <li className="flex gap-3">
                      <span className="shrink-0 font-bold text-red-500 bg-red-100 dark:bg-red-900/30 px-2 py-0.5 rounded text-xs h-fit mt-0.5">
                        {t("scriptGuide.scriptA", "剧本 A")}
                      </span>
                      <span className="text-slate-700 dark:text-slate-300">
                        {t("scriptGuide.philosophy.sec2.scriptA", "默认模式（暴君）：你大吼“家里成猪窝了你看不见吗？”，把厨房变成了战场。")}
                      </span>
                    </li>
                    <li className="flex gap-3">
                      <span className="shrink-0 font-bold text-orange-500 bg-orange-100 dark:bg-orange-900/30 px-2 py-0.5 rounded text-xs h-fit mt-0.5">
                        {t("scriptGuide.scriptB", "剧本 B")}
                      </span>
                      <span className="text-slate-700 dark:text-slate-300">
                        {t("scriptGuide.philosophy.sec2.scriptB", "惯性模式（受害者）：你一边洗一边流泪，觉得全家都把你当保姆，把厨房变成了哭墙。")}
                      </span>
                    </li>
                    <li className="flex gap-3">
                      <span className="shrink-0 font-bold text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 rounded text-xs h-fit mt-0.5">
                        {t("scriptGuide.scriptC", "剧本 C")}
                      </span>
                      <span className="text-slate-700 dark:text-slate-300">
                        {t("scriptGuide.philosophy.sec2.scriptC", "觉醒模式（和平观察者）：你按下了暂停。你看到了那只是几个碗，不是他不爱你的证据。你平静地表达需求，把厨房变成了沟通的桥梁。")}
                      </span>
                    </li>
                  </ul>
                </section>

                {/* Section 3: 命运与选择 */}
                <section>
                  <h3 className="font-bold text-lg text-primary mb-3">
                    {t("scriptGuide.philosophy.sec3.title", "所谓的“命运”，就是你每一次选择的总和")}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("scriptGuide.philosophy.sec3.content", "生活中的痛苦，往往不是因为事情本身，而是因为我们失去了选择权，被锁死在了那个最低能量的剧本里。人生编辑器不是要教你大道理，而是要递给你那把剪刀。它不仅是一次心理测试，更是一次灵魂的越狱。")}
                  </p>
                </section>

                {/* Section 4: 唤醒 */}
                <section className="bg-amber-50 dark:bg-amber-950/30 p-5 rounded-xl border border-amber-100 dark:border-amber-900/50">
                  <h3 className="font-bold text-lg text-amber-700 dark:text-amber-500 mb-3">
                    {t("scriptGuide.philosophy.sec4.title", "从今天起，拿回你的场记板")}
                  </h3>
                  <p className="text-amber-900/80 dark:text-amber-200/80 leading-relaxed mb-4">
                    {t("scriptGuide.philosophy.sec4.content", "别再让情绪劫持你的大脑，别再让惯性推着你走。每一次被激怒、被忽视、被挑战的瞬间，都是一次改写剧情的机会。")}
                  </p>
                  <p className="font-bold text-center text-amber-600 text-lg mt-6">
                    {t("scriptGuide.philosophy.sec4.closing", "醒过来。摄影机正在滚动。Action。")}
                  </p>
                </section>

              </div>
            </TabsContent>

            {/* --- Tab 2: 角色图鉴 (Updated Inner Monologues) --- */}
            <TabsContent value="archetypes" className="animate-in fade-in-50 slide-in-from-bottom-2 focus-visible:outline-none">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-6">
                
                {/* 求生剧组 (左侧) */}
                <div className="rounded-xl bg-slate-900 text-slate-100 p-5 shadow-inner">
                  <h4 className="font-bold text-red-400 mb-4 flex items-center gap-2 border-b border-white/10 pb-2">
                    {t("scriptGuide.archetypes.survival.title", "🔻 求生剧组")}
                    <span className="text-xs font-normal text-slate-400 ml-auto">{t("scriptGuide.survival.note", "基于恐惧/匮乏")}</span>
                  </h4>
                  <ul className="space-y-4 text-sm">
                    <ArchetypeItem 
                      name={t("scriptGuide.archetypes.survival.invisible", "隐形人 (20 · 羞愧 Shame)")} 
                      desc={t("scriptGuide.archetypes.survival.invisible_monologue", "“只要我不说话，就没有人能攻击我。”")} 
                    />
                    <ArchetypeItem 
                      name={t("scriptGuide.archetypes.survival.sinner", "罪人 (30 · 内疚 Guilt)")} 
                      desc={t("scriptGuide.archetypes.survival.sinner_monologue", "“我是个糟糕的人，我不配拥有好结局。”")} 
                    />
                    <ArchetypeItem 
                      name={t("scriptGuide.archetypes.survival.bystander", "旁观者 (50 · 冷漠 Apathy)")} 
                      desc={t("scriptGuide.archetypes.survival.bystander_monologue", "“无所谓，反正一切都和我没关系。”")} 
                    />
                    <ArchetypeItem 
                      name={t("scriptGuide.archetypes.survival.victim", "受害者 (75 · 悲伤 Grief)")} 
                      desc={t("scriptGuide.archetypes.survival.victim_monologue", "“全世界都欠我的，但我无能为力。”")} 
                    />
                    <ArchetypeItem 
                      name={t("scriptGuide.archetypes.survival.coward", "懦夫 (100 · 恐惧 Fear)")} 
                      desc={t("scriptGuide.archetypes.survival.coward_monologue", "“外面太危险了，还是维持现状吧。”")} 
                    />
                    <ArchetypeItem 
                      name={t("scriptGuide.archetypes.survival.addict", "瘾君子 (125 · 欲望 Desire)")} 
                      desc={t("scriptGuide.archetypes.survival.addict_monologue", "“只有得到那个东西，我才能活下去。”")} 
                    />
                    <ArchetypeItem 
                      name={t("scriptGuide.archetypes.survival.tyrant", "暴君 (150 · 愤怒 Anger)")} 
                      desc={t("scriptGuide.archetypes.survival.tyrant_monologue", "“都听我的！谁也别想挑战我。”")} 
                    />
                    <ArchetypeItem 
                      name={t("scriptGuide.archetypes.survival.performer", "演员 (175 · 骄傲 Pride)")} 
                      desc={t("scriptGuide.archetypes.survival.performer_monologue", "“看我！看我！我必须是最完美的那个。”")} 
                    />
                  </ul>
                </div>

                {/* 创造剧组 (右侧) */}
                <div className="rounded-xl bg-gradient-to-br from-indigo-50 via-purple-50 to-amber-50 dark:from-indigo-950/30 dark:to-amber-950/30 p-5 shadow-sm border">
                  <h4 className="font-bold text-amber-600 dark:text-amber-400 mb-4 flex items-center gap-2 border-b border-amber-200 dark:border-amber-800 pb-2">
                    {t("scriptGuide.archetypes.creative.title", "💎 创造剧组")}
                    <span className="text-xs font-normal text-slate-500 dark:text-slate-400 ml-auto">{t("scriptGuide.creative.note", "基于爱/丰盛")}</span>
                  </h4>
                  <ul className="space-y-4 text-sm">
                    <ArchetypeItem 
                      variant="light"
                      name={t("scriptGuide.archetypes.creative.explorer", "探险家 (200 · 勇气 Courage)")} 
                      desc={t("scriptGuide.archetypes.creative.explorer_monologue", "“害怕也没关系，重要的是迈出这一步。”")} 
                    />
                    <ArchetypeItem 
                      variant="light"
                      name={t("scriptGuide.archetypes.creative.doer", "实干家 (250/310 · 意愿 Willingness)")} 
                      desc={t("scriptGuide.archetypes.creative.doer_monologue", "“问题就是机会。我有方案，跟我来。”")} 
                    />
                    <ArchetypeItem 
                      variant="light"
                      name={t("scriptGuide.archetypes.creative.sage", "智者 (350/400 · 理智 Reason)")} 
                      desc={t("scriptGuide.archetypes.creative.sage_monologue", "“情绪只是表象，我看透了背后的逻辑。”")} 
                    />
                    <ArchetypeItem 
                      variant="light"
                      name={t("scriptGuide.archetypes.creative.healer", "疗愈者 (500 · 爱 Love)")} 
                      desc={t("scriptGuide.archetypes.creative.healer_monologue", "“不需要改变什么，我爱当下真实的你。”")} 
                    />
                    <ArchetypeItem 
                      variant="light"
                      name={t("scriptGuide.archetypes.creative.celebrator", "庆祝者 (540 · 喜悦 Joy)")} 
                      desc={t("scriptGuide.archetypes.creative.celebrator_monologue", "“活着本身，就是一场值得开香槟的盛宴。”")} 
                    />
                    <ArchetypeItem 
                      variant="light"
                      name={t("scriptGuide.archetypes.creative.observer", "和平的观察者 (600 · 和平 Peace)")} 
                      desc={t("scriptGuide.archetypes.creative.observer_monologue", "“无论外界如何，我的内心始终安定如水。”")} 
                    />
                    <ArchetypeItem 
                      variant="light"
                      name={t("scriptGuide.archetypes.creative.enlightened", "开悟者 (700+ · 开悟 Enlightenment)")} 
                      desc={t("scriptGuide.archetypes.creative.enlightened_monologue", "“我与万物合一，超越了小我的界限。”")} 
                    />
                  </ul>
                </div>
              </div>

              <div className="mt-8 text-center pb-4">
                <Button 
                  size="lg" 
                  className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-8 shadow-lg shadow-amber-500/20 transition-all hover:scale-105"
                  onClick={() => setOpen(false)}
                >
                  <Sparkles className="mr-2 h-5 w-5" /> 
                  {t("common.start", "Action! 开始觉察")}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// 辅助组件：简化列表项代码
function ArchetypeItem({ name, desc, variant = "dark" }: { name: string; desc: string; variant?: "dark" | "light" }) {
  const nameColor = variant === "dark" ? "text-slate-200" : "text-slate-900 dark:text-slate-100";
  // 使用更亮的颜色强调潜台词，增加引号增强语境感
  const descColor = variant === "dark" ? "text-slate-400 italic" : "text-slate-600 dark:text-slate-400 italic";
  
  return (
    <li>
      <span className={`font-bold block text-sm ${nameColor}`}>{name}</span>
      <span className={`block text-xs mt-1 leading-normal ${descColor}`}>{desc}</span>
    </li>
  );
}